import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Breadcrumbs,
  Link,
  Chip
} from '@mui/material';
import {
  Add,
  Psychology,
  Home
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '../../hooks/useApi';
import { PromptsManagement } from '../../components/admin/PromptsManagement';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface LLMPrompt {
  promptKey: string;
  modelId: number;
  model?: {
    id: number;
    provider: string;
    modelName: string;
    alias?: string;
    family: string;
    release: string;
  };
  systemPrompt?: string;
  prompt: string;
  temperature: number;
  maxTokens: number;
  pdfCharacters: number;
  description?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const PromptsAdmin: React.FC = () => {
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const navigate = useNavigate();
  const [selectedPrompt, setSelectedPrompt] = useState<LLMPrompt | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Show loading spinner while user data is being fetched
  if (isLoadingUser) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have admin permission (after loading completes)
  if (!user?.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }

  // Fetch prompts
  const { data: promptsData, isLoading, error, refetch } = useQuery({
    queryKey: ['prompts'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/prompts`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch prompts');
      }
      return await response.json();
    }
  });

  const prompts = promptsData?.data || [];

  const handleEditPrompt = (prompt: LLMPrompt) => {
    setSelectedPrompt(prompt);
    setIsCreating(false);
    setIsEditorOpen(true);
  };

  const handleCreatePrompt = () => {
    setSelectedPrompt(null);
    setIsCreating(true);
    setIsEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setIsEditorOpen(false);
    setSelectedPrompt(null);
    setIsCreating(false);
    refetch(); // Refresh the list
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">LLM Prompts</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Psychology sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                LLM Prompts Management
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreatePrompt}
            >
              Create New Prompt
            </Button>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Configure AI prompts for MarketCompass report processing
          </Typography>
        </Box>

        {/* Info Box */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.lighter' }}>
          <Typography variant="body2" color="info.dark">
            <strong>Note:</strong> Changes to prompts take effect immediately. Test changes carefully before saving.
            Active prompts are used for processing new MarketCompass reports.
          </Typography>
        </Paper>

        {/* Content */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Paper sx={{ p: 3, bgcolor: 'error.lighter' }}>
            <Typography color="error">
              Error loading prompts: {(error as any).message}
            </Typography>
          </Paper>
        ) : (
          <>
            {/* Stats */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
              <Chip 
                label={`${prompts.length} Total Prompts`} 
                color="primary" 
                variant="outlined" 
              />
              <Chip 
                label={`${prompts.filter((p: LLMPrompt) => p.isActive).length} Active`} 
                color="success" 
                variant="outlined" 
              />
              <Chip 
                label={`${prompts.filter((p: LLMPrompt) => !p.isActive).length} Inactive`} 
                color="default" 
                variant="outlined" 
              />
            </Box>

            {/* Prompts Grid */}
            <PromptsManagement
              prompts={prompts}
              onEditPrompt={handleEditPrompt}
              isEditorOpen={isEditorOpen}
              selectedPrompt={selectedPrompt}
              isCreating={isCreating}
              onCloseEditor={handleCloseEditor}
            />
          </>
        )}
      </Box>
    </Container>
  );
};